// п. 23 (`DEV-68`, ФЗ-152, РГ-08 ред. 2.1). Экран администратора: внесение
// пользователя по договору и работа с приглашением.
//
// Машиноместо выбирается **только из заведённых на объекте** — поля для
// свободного ввода нет (`РГ-07`): иначе в договор попадало бы место, которого в
// системе не существует. Основание хранения — номер **и** дата договора; без
// любого из них «Внести» недоступна.
//
// Отказ «машиноместо занято» показывается **у поля места**: это причина, которую
// человек может устранить выбором другого места, а не общая ошибка формы.

import React, { FC, useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import { Context } from "../../index";
import ButtonComponent from "../../components/buttons/ButtonComponent";
import InvitationService from "../../services/InvitationService";
import MachinePlaceService, { IMachinePlace } from "../../services/MachinePlaceService";
import UserService from "../../services/UserService";

interface IInvitedRow {
  id: number;
  email: string | null;
  account_state: string | null;
}

const STATE_LABELS: Record<string, string> = {
  pending: "приглашён",
  active: "активен",
  cancelled: "аннулирована",
};

/** Состояние учётной записи словами: экран не показывает кодов сервера. */
export function stateLabel(state?: string | null): string {
  return STATE_LABELS[String(state)] || "—";
}

/** Отказ по месту — тот, что показывается у поля места, а не общей ошибкой. */
export function isPlaceError(message: string): boolean {
  return message.toLowerCase().includes("машиноместо");
}

function messageOf(error: any, fallback: string): string {
  return error?.response?.data?.message || fallback;
}

const InvitationsPage: FC = observer(() => {
  const { store } = useContext(Context);

  const isRoot = store.user?.role === "root";

  const [plantId, setPlantId] = useState<string>("");
  const [places, setPlaces] = useState<IMachinePlace[]>([]);
  const [placeId, setPlaceId] = useState<string>("");

  const [email, setEmail] = useState<string>("");
  const [contractNumber, setContractNumber] = useState<string>("");
  const [contractDate, setContractDate] = useState<string>("");

  const [placeError, setPlaceError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [rows, setRows] = useState<IInvitedRow[]>([]);

  const loadRows = async () => {
    try {
      const response = await UserService.fetchUsers();

      setRows(
        (response.data as any[])
          .filter(
            (user) =>
              user.account_state === "pending" || user.account_state === "active",
          )
          .map((user) => ({
            id: Number(user.id),
            email: user.email ?? null,
            account_state: user.account_state ?? null,
          })),
      );
    } catch (error: any) {
      setFormError(messageOf(error, "не удалось получить список внесённых"));
    }
  };

  useEffect(() => {
    if (!isRoot) return undefined;

    loadRows();

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoot]);

  const choosePlant = async (value: string) => {
    setPlantId(value);
    setPlaceId("");
    setPlaces([]);
    setPlaceError(null);

    if (!value) return;

    try {
      const response = await MachinePlaceService.list(Number(value));
      setPlaces(response.data);
    } catch (error: any) {
      setPlaceError(messageOf(error, "не удалось получить машиноместа объекта"));
    }
  };

  const canInvite =
    Boolean(email.trim()) &&
    Boolean(contractNumber.trim()) &&
    Boolean(contractDate) &&
    Boolean(placeId) &&
    !submitting;

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canInvite) return;

    setSubmitting(true);
    setPlaceError(null);
    setFormError(null);
    setNotice(null);

    try {
      const companyId =
        store.plants?.find((plant) => String(plant.id) === plantId)?.company_id ??
        null;

      const response = await InvitationService.invite({
        email: email.trim(),
        contractNumber: contractNumber.trim(),
        contractDate,
        machinePlaceId: Number(placeId),
        // `АУД-Н229`: у объекта может не быть компании. Поле тогда **не
        // отправляем вовсе**: сервер читает `null` как значение и отвечает 400,
        // хотя компании просто нет.
        ...(companyId ? { companyId } : {}),
      });

      setNotice(
        response.data.invitationSent
          ? "внесено, приглашение отправлено"
          : "внесено, письмо не ушло — повторите приглашение",
      );

      setEmail("");
      setContractNumber("");
      setContractDate("");
      setPlaceId("");

      await loadRows();
    } catch (error: any) {
      const message = messageOf(error, "не удалось внести пользователя");

      if (isPlaceError(message)) {
        setPlaceError(message);
      } else {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (userId: number) => {
    setFormError(null);
    setNotice(null);

    try {
      await InvitationService.resend(userId);
      setNotice("приглашение отправлено повторно");
      await loadRows();
    } catch (error: any) {
      setFormError(messageOf(error, "не удалось повторить приглашение"));
    }
  };

  const revoke = async (userId: number) => {
    setFormError(null);
    setNotice(null);

    try {
      await InvitationService.revoke(userId);
      setNotice("приглашение отозвано");
      // Строка обновляется **на месте**: отозванная запись остаётся видимой как
      // «аннулирована» и не исчезает из списка внесённых.
      setRows((prev) =>
        prev.map((row) =>
          row.id === userId ? { ...row, account_state: "cancelled" } : row,
        ),
      );
    } catch (error: any) {
      setFormError(messageOf(error, "не удалось отозвать приглашение"));
    }
  };

  if (!isRoot) {
    return (
      <div data-page="invitations" data-no-access>
        <h2>Внесение по договору</h2>
        <div>раздел доступен только роли «root»</div>
      </div>
    );
  }

  return (
    <div data-page="invitations">
      <h2>Внесение по договору</h2>

      <form onSubmit={invite} data-form="invite">
        <div className="loginForm_form">
          <label htmlFor="invite-plant">объект:</label>
          <select
            id="invite-plant"
            value={plantId}
            onChange={(e) => choosePlant(e.target.value)}
          >
            <option value="">— выберите объект —</option>
            {(store.plants || []).map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.name}
              </option>
            ))}
          </select>
        </div>

        <div className="loginForm_form">
          <label htmlFor="invite-place">машиноместо:</label>
          {/* Только выбор из заведённых: свободного ввода места нет. */}
          <select
            id="invite-place"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            disabled={!plantId}
          >
            <option value="">— выберите машиноместо —</option>
            {places.map((place) => (
              <option key={place.id} value={place.id}>
                {place.number || place.description || place.id}
              </option>
            ))}
          </select>
        </div>

        {placeError ? (
          <div className="loginForm_error" role="alert" data-place-error>
            {placeError}
          </div>
        ) : null}

        <div className="loginForm_form">
          <label htmlFor="invite-email">адрес почты:</label>
          <input
            className="loginForm_input"
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoCapitalize="none"
          />
        </div>

        <div className="loginForm_form">
          <label htmlFor="invite-contract-number">номер договора:</label>
          <input
            className="loginForm_input"
            id="invite-contract-number"
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
          />
        </div>

        <div className="loginForm_form">
          <label htmlFor="invite-contract-date">дата договора:</label>
          <input
            className="loginForm_input"
            id="invite-contract-date"
            type="date"
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
          />
        </div>

        <div className="loginForm_btns">
          <ButtonComponent title="Внести" size="100%" disabled={!canInvite} />
        </div>
      </form>

      {formError ? (
        <div className="loginForm_error" role="alert" data-form-error>
          {formError}
        </div>
      ) : null}

      {notice ? (
        <div className="loginForm_answer" role="status" data-notice>
          {notice}
        </div>
      ) : null}

      <h3>Внесённые по договору</h3>

      <table data-invited-list>
        <thead>
          <tr>
            <th>адрес</th>
            <th>состояние</th>
            <th>действия</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} data-user-id={row.id}>
              <td>{row.email}</td>
              <td data-state={row.account_state}>{stateLabel(row.account_state)}</td>
              <td>
                {row.account_state === "pending" ? (
                  <>
                    <ButtonComponent
                      title="Повторить"
                      type="out"
                      onClick={() => resend(row.id)}
                    />
                    <ButtonComponent
                      title="Отозвать"
                      type="out"
                      onClick={() => revoke(row.id)}
                    />
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default InvitationsPage;
